import type {
  CompositeFilterDescriptor,
  FilterDescriptor,
} from "@progress/kendo-data-query";
import type { FilterExpression } from "@/Domain/FilterExpression";
import type { FilterGroup } from "@/Domain/FilterGroup";
import type { FilterField } from "@/Domain/FilterField";
import { EnumFilterStatementConnector } from "@/Domain/EnumFilterStatementConnector";
import type { ValueFieldConvertOption } from "@/Domain/ValueFieldConvertOption";
import { FilterOperatorType } from "@/Domain/FilterOperatorType";

export interface GridFilterOperator {
  text: string;
  operator: string;
}

export const getJsonFilter = function (
  filter: CompositeFilterDescriptor | null,
  convertOptions: ValueFieldConvertOption[] = []
) {
  const filterModel = {
    FilterGroups: [],
    BooleanLogicOperator: EnumFilterStatementConnector.And,
  } as FilterExpression;

  if (filter === null) return filterModel;

  filter.filters.forEach((f) => {
    const cf = f as CompositeFilterDescriptor;
    if (cf.filters) {
      const filterGroup = {
        FilterFields: [],
        BooleanLogicOperator:
          cf.logic === "and"
            ? EnumFilterStatementConnector.And
            : EnumFilterStatementConnector.Or,
        FieldName: (cf.filters[0] as FilterDescriptor).field,
        InternalFieldName: "",
      } as FilterGroup;
      cf.filters.forEach((ff) => {
        const fd = ff as FilterDescriptor;
        let value = fd.value;
        let operator = fd.operator;
        const option = convertOptions.find(
          (o) => o.column === filterGroup.FieldName
        );

        if (option) value = option.convertBack(value);

        if (option && option.operatorConvert)
          operator = option.operatorConvert(value);

        if (
          operator ===
          getFilterOperator(FilterOperatorType.IsNull.toString())?.operator
        )
          operator = "Is null or whitespace";
        else if (
          operator ===
          getFilterOperator(FilterOperatorType.IsNotNull.toString())?.operator
        )
          operator = "Is not null or whitespace";

        const filterField = {
          Operator: operator,
          Value: value,
        } as FilterField;
        filterGroup.FilterFields.push(filterField);
      });
      filterModel.FilterGroups.push(filterGroup);
    }
  });
  console.debug("filtersHelper, getJsonFilter(), filterModel", filterModel);
  return filterModel;
};

export const getJsonFilterString = function (
  filter: CompositeFilterDescriptor | null,
  convertOptions: ValueFieldConvertOption[] = []
) {
  return JSON.stringify(getJsonFilter(filter, convertOptions));
};

export const valueFilterConvert = function (
  options: ValueFieldConvertOption[],
  column: string,
  items: any[]
) {
  const option = options.find((o) => o.column === column);
  if (!option) return items;
  return items.map((item) => {
    item[column] = option.convert(item[column]);
    return item;
  });
};

export const getValueFilterDistinct = function (column: string, items: any[]) {
  return items.filter(
    (item, index, self) =>
      index === self.findIndex((t) => t[column] === item[column])
  );
};

export const FilterOperators = [
  { text: FilterOperatorType.Contains.toString(), operator: "contains" },
  {
    text: FilterOperatorType.NotContains.toString(),
    operator: "doesnotcontain",
  },
  { text: FilterOperatorType.Equal.toString(), operator: "eq" },
  { text: FilterOperatorType.NotEqual.toString(), operator: "neq" },
  { text: FilterOperatorType.StartsWith.toString(), operator: "startswith" },
  { text: FilterOperatorType.EndsWith.toString(), operator: "endswith" },
  { text: FilterOperatorType.GreaterThanOrEqual.toString(), operator: "gte" },
  { text: FilterOperatorType.GreaterThan.toString(), operator: "gt" },
  { text: FilterOperatorType.LessThanOrEqual.toString(), operator: "lte" },
  { text: FilterOperatorType.LessThan.toString(), operator: "lt" },
  { text: FilterOperatorType.AfterOrEqual.toString(), operator: "gte" },
  { text: FilterOperatorType.After.toString(), operator: "gt" },
  { text: FilterOperatorType.Before.toString(), operator: "lt" },
  { text: FilterOperatorType.BeforeOrEqual.toString(), operator: "lte" },
  { text: FilterOperatorType.IsNull.toString(), operator: "isnull" },
  { text: FilterOperatorType.IsNotNull.toString(), operator: "isnotnull" },
] as GridFilterOperator[];

export const getFilterOperator = function (type: string) {
  return FilterOperators.find((fo) => fo.text === type);
};

export const getFilterOperators = function (types: string[]) {
  return FilterOperators.filter((fo) => types.includes(fo.text));
};

export const getFilterOperatorsForString = function () {
  const types = [
    FilterOperatorType.Contains.toString(),
    FilterOperatorType.NotContains.toString(),
    FilterOperatorType.Equal.toString(),
    FilterOperatorType.NotEqual.toString(),
    FilterOperatorType.StartsWith.toString(),
    FilterOperatorType.EndsWith.toString(),
    FilterOperatorType.IsNull.toString(),
    FilterOperatorType.IsNotNull.toString(),
  ];
  return getFilterOperators(types);
};

export const getFilterOperatorsForNumber = function () {
  const types = [
    FilterOperatorType.Equal.toString(),
    FilterOperatorType.NotEqual.toString(),
    FilterOperatorType.GreaterThanOrEqual.toString(),
    FilterOperatorType.GreaterThan.toString(),
    FilterOperatorType.LessThanOrEqual.toString(),
    FilterOperatorType.LessThan.toString(),
  ];
  return getFilterOperators(types);
};

export const getFilterOperatorsForDate = function () {
  // AfterOrEqual and BeforeOrEqual are not supported
  const types = [
    FilterOperatorType.Equal.toString(),
    FilterOperatorType.NotEqual.toString(),
    FilterOperatorType.After.toString(),
    FilterOperatorType.Before.toString(),
  ];
  return getFilterOperators(types);
};

export const getFilterOperatorsForBoolean = function () {
  const types = [FilterOperatorType.Equal.toString()];
  return getFilterOperators(types);
};

export const getFilterOperatorsForEnum = function () {
  const types = [
    FilterOperatorType.Equal.toString(),
    FilterOperatorType.NotEqual.toString(),
  ];
  return getFilterOperators(types);
};

export const isFilterEmpty = function (filter: CompositeFilterDescriptor) {
  if (!filter || filter.filters.length === 0) return true;
  return filter.filters.every((f) => {
    const fd = f as CompositeFilterDescriptor;
    return !fd?.filters;
  });
};

export const dateConvert = function (value: string) {
  return new Date(value).toLocaleDateString();
};

export const dateConvertBack = function (value: string) {
  return `${value} 00:00:00`;
};

export const dateTimeConvert = function (value: string) {
  return new Date(value).toLocaleString("en-US");
}

export const dateTimeConvertBack = function (value: string) {
  return value;
};

export const boolYNConvert = function (value: string) {
  return value ? "Y" : "N";
};

export const boolYNConvertBack = function (value: string) {
  return value?.toLowerCase() === "y";
};

export const boolYNConvertBackToString = function (value: string) {
  return boolYNConvertBack(value).toString();
};

export const FilterValuesTake = 1000;
